/**
 * Custom 3rd-Party App Integrations & Overrides
 * - Adjusts Tidio chat widget position dynamically on mobile:
 *   Elevates Tidio when `.product-sticky-form__card` is visible,
 *   and restores default position when it is hidden.
 */

(() => {
  const MOBILE_BREAKPOINT = 767;

  /**
   * Adjusts Tidio chat position dynamically based on sticky buy bar visibility on mobile.
   */
  function adjustTidioChatPosition() {
    const stickyForm = document.querySelector('product-sticky-form');
    if (!stickyForm) return;

    const card = stickyForm.querySelector('.product-sticky-form__card') || stickyForm;
    let lastCalculatedHeight = 70;

    /**
     * Calculate current height of the sticky form bar
     */
    function getStickyBarHeight() {
      const height = Math.round(card.offsetHeight || card.getBoundingClientRect().height || stickyForm.offsetHeight || 0);
      if (height > 0) {
        lastCalculatedHeight = height;
      }
      return lastCalculatedHeight;
    }

    /**
     * Check if the sticky form card is currently visible
     */
    function isCardVisible() {
      if (!card) return false;
      const style = window.getComputedStyle(card);
      if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) {
        return false;
      }
      return true;
    }

    let isApplying = false;

    /**
     * Applies or resets inset style inside Tidio shadow DOM
     */
    function applyStyle(shadowRoot) {
      if (isApplying) return;

      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      const isVisible = isCardVisible();
      const shouldElevate = isMobile && isVisible;
      const barHeight = getStickyBarHeight();
      const targetInset = shouldElevate ? `0px auto ${barHeight}px 0px` : '0px auto 0px 0px';
      const targetBottom = shouldElevate ? `${barHeight}px` : '0px';

      const chatRoot =
        shadowRoot.querySelector('.grid-layout.widget-position-left') ||
        shadowRoot.getElementById('tidio-chat-root');

      let styleEl = shadowRoot.getElementById('custom-tidio-position-style');

      const currentInsetStyle = chatRoot ? chatRoot.style.getPropertyValue('inset') : null;
      const currentRule = styleEl ? styleEl.textContent : '';

      const desiredRule = isMobile
        ? `
        @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
          .grid-layout.widget-position-left {
            inset: ${targetInset} !important;
            bottom: ${targetBottom} !important;
            transition: inset 0.25s ease, bottom 0.25s ease !important;
          }
        }
      `
        : '';

      // Skip mutation if already matching desired rule to prevent unnecessary re-renders
      if (
        currentRule.trim() === desiredRule.trim() &&
        (!chatRoot || (!isMobile && !currentInsetStyle) || (isMobile && currentInsetStyle === targetInset))
      ) {
        return;
      }

      try {
        isApplying = true;

        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'custom-tidio-position-style';
          shadowRoot.appendChild(styleEl);
        }
        styleEl.textContent = desiredRule;

        if (chatRoot) {
          if (isMobile) {
            chatRoot.style.setProperty('inset', targetInset, 'important');
            chatRoot.style.setProperty('bottom', targetBottom, 'important');
            chatRoot.style.setProperty('transition', 'inset 0.25s ease, bottom 0.25s ease', 'important');
          } else {
            chatRoot.style.removeProperty('inset');
            chatRoot.style.removeProperty('bottom');
            chatRoot.style.removeProperty('transition');
          }
        }
      } finally {
        setTimeout(() => {
          isApplying = false;
        }, 50);
      }
    }

    /**
     * Triggers style application on Tidio shadowRoot if available
     */
    function updateTidioPosition() {
      const tidioEl = document.getElementById('tidio-chat');
      if (tidioEl && tidioEl.shadowRoot) {
        applyStyle(tidioEl.shadowRoot);
      }
    }

    let shadowObserver = null;

    /**
     * Observes Tidio shadow root and applies custom styles
     */
    function setupTidioShadowRoot(tidioEl) {
      if (!tidioEl || !tidioEl.shadowRoot) {
        return false;
      }

      applyStyle(tidioEl.shadowRoot);

      if (!shadowObserver) {
        shadowObserver = new MutationObserver(() => {
          applyStyle(tidioEl.shadowRoot);
        });

        shadowObserver.observe(tidioEl.shadowRoot, {
          childList: true,
          subtree: true,
        });
      }

      return true;
    }

    /**
     * Looks for #tidio-chat and initializes adjustment
     */
    function tryInitTidio() {
      const tidioEl = document.getElementById('tidio-chat');
      if (tidioEl) {
        if (setupTidioShadowRoot(tidioEl)) {
          return true;
        }
      }
      return false;
    }

    // Try immediately
    if (!tryInitTidio()) {
      // 1. Listen for Tidio ready event
      document.addEventListener(
        'tidioChat-ready',
        () => {
          tryInitTidio();
        },
        { once: true }
      );

      if (window.tidioChatApi) {
        window.tidioChatApi.on('ready', () => {
          tryInitTidio();
        });
      }

      // 2. Observe DOM in case Tidio element is injected later
      const bodyObserver = new MutationObserver((mutations, observer) => {
        if (tryInitTidio()) {
          observer.disconnect();
        }
      });

      bodyObserver.observe(document.body || document.documentElement, {
        childList: true,
        subtree: true,
      });

      // 3. Interval fallback for the first 10 seconds
      let retries = 0;
      const interval = setInterval(() => {
        retries++;
        if (tryInitTidio() || retries >= 40) {
          clearInterval(interval);
        }
      }, 250);
    }

    // Observe changes to the card's visibility / style / class
    const cardObserver = new MutationObserver(() => {
      updateTidioPosition();
    });

    cardObserver.observe(card, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    // Also observe the stickyForm container itself in case of class or style changes
    if (card !== stickyForm) {
      cardObserver.observe(stickyForm, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }

    // Sync on scroll with requestAnimationFrame for smooth detection
    let scrollRaf = null;
    window.addEventListener(
      'scroll',
      () => {
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
        scrollRaf = requestAnimationFrame(() => {
          updateTidioPosition();
        });
      },
      { passive: true }
    );

    // Recalculate when sticky bar size changes (e.g. dynamic content/variants)
    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => {
        updateTidioPosition();
      });
      resizeObserver.observe(card);
    }

    // Recalculate on window resize / orientation change
    let resizeTimeout = null;
    window.addEventListener('resize', () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateTidioPosition();
      }, 100);
    });

    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        updateTidioPosition();
      }, 200);
    });
  }

  // Expose globally if needed
  window.adjustTidioChatPosition = adjustTidioChatPosition;

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', adjustTidioChatPosition);
  } else {
    adjustTidioChatPosition();
  }
})();
